import { MouseEvent } from 'react';
import { action, computed, observable } from 'mobx';
import { ServiceViewWithModalViewController } from './ServiceViewWithModalViewController';
import { Config } from '~domain/entities/Config';
import { Store } from '~domain/entities/Store';
import { DeliveryOptions, StoreDeliveryOptions } from '../../constants/constants';
import { DOOR_STATUS } from '~api/BOPISApiInterfaces';
import { TransactionRepository } from '~repositories/TransactionRepository';
import { Transaction } from '~domain/entities/Transaction';
import { StoresRepository } from '../repositories/StoresRepository';
import { DCInventoryRepository } from '~repositories/DCInventoryRepository';
import { CourierRepository } from '~repositories/CourierRepository';
import { events, emit } from '@estee/elc-service-bus';

export class DeliveryMethodsViewController extends ServiceViewWithModalViewController {
    @observable public configService: Config;
    @observable public deliveryOption: DeliveryOptions;
    @observable public activeDeliveryOption: DeliveryOptions;
    @observable private transactionRepository: TransactionRepository;
    @observable public storesRepository: StoresRepository;
    @observable public transaction: Transaction;
    @observable public DCRepository: DCInventoryRepository;
    @observable public courierRepository: CourierRepository;

    constructor(
        configService: Config,
        transactionRepository: TransactionRepository,
        storesRepository: StoresRepository,
        DCRepository: DCInventoryRepository,
        courierRepository: CourierRepository
    ) {
        super();
        this.configService = configService;
        this.transactionRepository = transactionRepository;
        this.storesRepository = storesRepository;
        this.DCRepository = DCRepository;
        this.transaction = new Transaction();
        this.courierRepository = courierRepository;
    }

    @action
    public loaded = async () => {
        this.isLoading = true;
        await this.fetchDeliveryOption();
        this.isLoading = false;
    };

    @computed
    public get homeDeliveryAvailabilityMessage() {
        return this.DCRepository.getDCAvailabilityMessage();
    }

    @computed
    get courierStoreNotFound(): boolean {
        return this.deliveryOption === DeliveryOptions.COURIER && !this.courierStoreWithCartItems;
    }

    @computed
    get storesWithAvailableSku(): Store[] {
        const { stores } = this.storesRepository;

        return stores.filter((store) => {
            const skusOnhand = store.skusOnhand;

            if (skusOnhand && Object.values(skusOnhand).length > 0) {
                return Object.values(skusOnhand)[0].is_available;
            } else {
                return [];
            }
        });
    }

    @computed
    get storesWithAvailableItems(): Store[] {
        const { stores } = this.storesRepository;

        return stores.filter((store) => {
            const cartItems = store.cartItems || [];

            return Object.keys(cartItems).every((item) => cartItems[item].is_available);
        });
    }

    validStores(stores: Store[]) {
        const deliveryDistance = this.configService?.config?.postmatesMaxDeliveryDistance;

        return stores.filter(
            (store) =>
                store.geographical.distance <= deliveryDistance &&
                store.courierDelivery === DOOR_STATUS.ACTIVE
        );
    }

    sortedByDistanceStores(stores: Store[]) {
        const storesSortedByDistance =
            stores &&
            stores.sort(
                (firstStore, secondStore) =>
                    firstStore.geographical.distance - secondStore.geographical.distance
            );

        return storesSortedByDistance || [];
    }

    @computed
    get courierStore(): Store {
        const validStores = this.validStores(this.storesWithAvailableSku);
        const sortedStores = this.sortedByDistanceStores(validStores);

        return sortedStores[0];
    }

    @computed
    get isCourierDisabled(): boolean {
        const isDisabled = !Boolean(this.courierStore);

        return isDisabled;
    }

    @computed
    get courierStoreWithCartItems(): Store {
        const validStores = this.validStores(this.storesWithAvailableItems);
        const sortedStores = this.sortedByDistanceStores(validStores);

        return sortedStores[0];
    }

    @computed
    get isCourierEnabled() {
        return this.configService?.config?.courierEnabled;
    }

    @computed
    get isBopisEnabled() {
        return this.configService?.config?.bopisEnabled;
    }

    @computed
    get isFullAddressSearchEnabled() {
        return this.configService?.config?.fullAddressSearchEnabled;
    }

    @computed
    get selectedDeliveryOption() {
        return this.deliveryOption;
    }

    @computed
    public get bopisSelectedStore() {
        const id = this.storesRepository.selectedStoreId;
        const selectedStore = this.storesRepository.stores.find((store) => store.id === id);

        return selectedStore;
    }

    @computed
    public get isAvailableTomorrow() {
        return this.bopisSelectedStore?.isAvailableTomorrow;
    }

    @computed
    public get isAvailableToday() {
        return this.bopisSelectedStore?.isAvailableTodayBopis;
    }

    @computed
    public get isShoppable() {
        return this.DCRepository.isShoppable;
    }

    @computed
    public get isCallForPlaceOrderEnabled() {
        return this.configService?.config?.stores?.isCallForPlaceOrderEnabled;
    }

    @computed
    public get storePhoneNumber() {
        return this.bopisSelectedStore?.primaryPhone?.value || '';
    }

    @computed
    public get bopisStoreName() {
        const storeName = this.bopisSelectedStore && this.bopisSelectedStore.name;
        const city = this.bopisSelectedStore?.address?.city;
        const fullStoreName = storeName ? `${storeName} ${city}` : '';

        return fullStoreName;
    }

    @action
    fetchDeliveryOption = async () => {
        const transaction = await this.transactionRepository.loadTransaction();
        this.setTransaction(transaction);
        const deliveryOption = this.transaction?.deliveryOption;
        if (
            deliveryOption === DeliveryOptions.STANDARD &&
            !this.DCRepository?.isShoppable &&
            this.bopisSelectedStore
        ) {
            this.setSelectedDeliveryOption(DeliveryOptions.BOPIS);
        } else {
            this.setSelectedDeliveryOption(deliveryOption);
        }
    };

    @action
    public setSelectedDeliveryOption = (
        val: DeliveryOptions,
        delivery_type_title?: string
    ): void => {
        if (delivery_type_title) {
            emit(events.STORE_DELIVERY_TYPE_SELECTED, {
                eventAction: StoreDeliveryOptions.DELIVERY_TYPE,
                eventLabel: delivery_type_title
            });
        }
        if (
            !delivery_type_title &&
            !this.DCRepository?.isShoppable &&
            this.storesRepository.selectedStoreId
        ) {
            this.activeDeliveryOption = DeliveryOptions.BOPIS;
        } else {
            this.activeDeliveryOption = val;
        }
        if (
            (!this.courierStore && val === DeliveryOptions.COURIER) ||
            (!this.storesRepository.selectedStoreId && val === DeliveryOptions.BOPIS)
        ) {
            this.deliveryOption = DeliveryOptions.STANDARD;
        } else {
            this.deliveryOption = val;
        }
        this.transactionRepository.setDeliveryOption(val);
    };

    @action
    setTransaction = (transaction: Transaction): void => {
        this.transaction = transaction;
    };

    @action
    public onOpenStoresModal = (e: MouseEvent) => {
        e.stopPropagation();
        this.onExportedServiceViewWithModalClicked();
        emit(events.STORE_CHANGE_LINK_CLICKED, {
            eventAction: StoreDeliveryOptions.CHANGE_STORE,
            eventLabel: StoreDeliveryOptions.CLICK
        });
    };

    @computed
    public get deliveryMethodsPrice() {
        return this.storesRepository.deliveryPrice;
    }
}
